import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, Dropdown, DropdownMenuItem} from 'views/components';
import {translate} from 'views/module';

import KanbanColumn from './column/KanbanColumn';

import backend from 'utils/backend';
import moment from 'moment';

const caseStatusFilter = {key: 'status', value: ['OPEN', 'PENDING', 'COMPLETE']};
const caseSort = {sortKey: 'updatedAt', sortDirection: 'DESC'};
const caseDateRanges = [
  {key: 'TODAY', days: 1},
  {key: 'LAST_3_DAYS', days: 3},
  {key: 'LAST_7_DAYS', days: 7},
  {key: 'LAST_28_DAYS', days: 28},
];
const caseTypeGroups = [
  {key: 'ALL', caseTypes: []},
  {
    key: 'SALES',
    caseTypes: [
      'LEAD_QUALIFICATION',
    ],
  },
  {
    key: 'RECRUITING',
    caseTypes: [
      'CAREGIVER_JOB_INQUIRY',
      'JOB_POSTING_NOTIFICATION',
      'CAREGIVER_PROFILE_COMPLETION',
    ],
  },
  {
    key: 'OPERATIONS',
    caseTypes: [
      'MISSING_PAY',
      'CAREGIVER_FAMILY_CHAT_MONITORING',
      'CAREGIVER_SHIFT_CANCELLATION',
      'TIMECLOCK',
      'CAREGIVER_RESCHEDULE_REQUEST',
      'CAREGIVER_DISTRESS_MONITORING',
    ],
  },
];

const CaseList = (props) => {
  const {
    statusProgressWrap: progressWrap,
  } = props;
  const [cases, setCases] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState();
  const [dateRangeKey, setDateRangeKey] = React.useState('LAST_7_DAYS');
  const [caseTypeGroupKey, setCaseTypeGroupKey] = React.useState('ALL');
  const [stage, setStage] = React.useState('FILL');
  const requestIdRef = React.useRef(0);

  const isLoading = stage === 'LOADING';
  const selectedDateRangeStart = React.useMemo(() => {
    const selectedDateRange = caseDateRanges.find((dateRange) => dateRange.key === dateRangeKey) || caseDateRanges[2];

    return moment().subtract(selectedDateRange.days - 1, 'days').startOf('day').toISOString();
  }, [dateRangeKey]);
  const caseFilters = React.useMemo(() => {
    const selectedCaseTypeGroup = caseTypeGroups.find((caseTypeGroup) => caseTypeGroup.key === caseTypeGroupKey);
    const filters = [caseStatusFilter];

    if (selectedCaseTypeGroup?.caseTypes?.length) {
      filters.push({
        key: 'caseType',
        value: selectedCaseTypeGroup.caseTypes,
      });
    }

    return filters;
  }, [caseTypeGroupKey]);

  const loadCases = React.useCallback(async (
      date,
      startKey,
      shouldReset = false,
      requestId = requestIdRef.current,
      showProgress = true,
  ) => {
    await progressWrap(async () => {
      const caseResponse = await backend.case.queryCaseFromDate(date, caseFilters, caseSort, startKey);

      if (requestId !== requestIdRef.current) return;

      setCases((currentCases) => {
        const nextCases = caseResponse?.list || [];
        return shouldReset ? nextCases : [
          ...currentCases,
          ...nextCases,
        ];
      });
      setLastEvaluatedKey(caseResponse?.lastEvaluatedKey);
    }, {setStage, showProgress});
  }, [caseFilters, progressWrap]);

  React.useEffect(() => {
    requestIdRef.current += 1;
    setCases([]);
    setLastEvaluatedKey();
    loadCases(selectedDateRangeStart, null, true, requestIdRef.current);

    return () => {
      requestIdRef.current += 1;
    };
  }, [caseTypeGroupKey, loadCases, selectedDateRangeStart]);

  const loadMoreCases = () => {
    if (!lastEvaluatedKey || isLoading) return;
    loadCases(selectedDateRangeStart, lastEvaluatedKey, false, requestIdRef.current, false);
  };

  const caseStatuses = [
    {status: 'OPEN', title: translate('case.Status.OPEN.ColumnTitle')},
    {status: 'PENDING', title: translate('case.Status.PENDING.ColumnTitle')},
    {status: 'COMPLETE', title: translate('case.Status.COMPLETE.ColumnTitle')},
  ];
  const getCaseUpdatedAt = (caseData) => {
    const updatedAtTime = new Date(caseData.updatedAt).getTime();

    return Number.isNaN(updatedAtTime) ? 0 : updatedAtTime;
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: {
          md: 'calc(99vh - 32px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          px: 3,
          pt: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 180,
            }}
          >
            <Dropdown
              disabled={isLoading}
              label={translate('case.List.DateRange.Label')}
              onChange={setDateRangeKey}
              value={dateRangeKey}
            >
              {
                caseDateRanges.map((dateRange) => (
                  <DropdownMenuItem
                    key={dateRange.key}
                    value={dateRange.key}
                  >
                    {translate(`case.List.DateRange.Options.${dateRange.key}`)}
                  </DropdownMenuItem>
                ))
              }
            </Dropdown>
          </Box>
          <Box
            sx={{
              width: 180,
            }}
          >
            <Dropdown
              disabled={isLoading}
              label={translate('case.List.CaseTypeGroup.Label')}
              onChange={setCaseTypeGroupKey}
              value={caseTypeGroupKey}
            >
              {
                caseTypeGroups.map((caseTypeGroup) => (
                  <DropdownMenuItem
                    key={caseTypeGroup.key}
                    value={caseTypeGroup.key}
                  >
                    {translate(`case.List.CaseTypeGroup.Options.${caseTypeGroup.key}`)}
                  </DropdownMenuItem>
                ))
              }
            </Dropdown>
          </Box>
        </Box>
        {
          lastEvaluatedKey && (
            <Button
              disabled={isLoading}
              onClick={loadMoreCases}
              sx={{
                'minWidth': '96px',
                'height': '32px',
                'px': '10px',
                'py': '8px',
                'border': '1px solid #D8D3CC',
                'borderRadius': '7px',
                'backgroundColor': '#FFFFFF',
                'color': '#6B6760',
                'textTransform': 'none',
                'boxShadow': 'none',
                '&:hover': {
                  'border': '1px solid #D8D3CC',
                  'backgroundColor': '#F7F4EF',
                  'boxShadow': 'none',
                },
              }}
              variant='outlined'
            >
              {translate('case.List.LoadMore')}
            </Button>
          )
        }
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          width: '100%',
          p: 3,
          overflowX: 'auto',
        }}
      >
        {
          caseStatuses.map((caseStatus) => {
            const statusCases = cases
                .filter((caseData) => caseData.status === caseStatus.status)
                .sort((firstCase, secondCase) => getCaseUpdatedAt(secondCase) - getCaseUpdatedAt(firstCase));

            return (
              <KanbanColumn
                key={caseStatus.status}
                title={caseStatus.title}
                cases={statusCases}
              />
            );
          })
        }
      </Box>
    </Box>
  );
};

CaseList.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(CaseList));
